import { useAuth } from "@/context/auth";
import DayProgress from "@/modules/mobileHome/DayProgress";
import HomeHeader from "@/modules/mobileHome/HomeHeader";
import MyEarnings from "@/modules/mobileHome/MyEarnings";
import NowCard, { InvoiceReminder } from "@/modules/mobileHome/NowCard";
import Suggestions, { Suggestion } from "@/modules/mobileHome/Suggestions";
import UpcomingAppointments, { UpcomingItem } from "@/modules/mobileHome/UpcomingAppointments";
import { compactRial, compactRialText, durationText, faNumber, isoDate, moneyNote, percentOf, todayTitle } from "@/modules/mobileHome/format";
import { HeroCard, HeroStat, HeroStats } from "@/modules/mobileHome/HeroCard";
import classes from "@/modules/mobileHome/home.module.scss";
import { DayGap, DayVisit, bookingTime, clockOf, minutesUntil, spanText, stylistDay } from "@/modules/mobileHome/stylistDay";
import { audienceSuggestion, stylistSuggestions } from "@/modules/mobileHome/suggestionList";
import { upcomingFromReport } from "@/modules/mobileHome/upcoming";
import useNow from "@/modules/mobileHome/useNow";
import useSectionBadges from "@/modules/mobileHome/useSectionBadges";
import { MerchantUserType } from "@/models/club";
import useGetMerchant from "@/pages/dashboard/profile/hooks/useGetMerchant";
import { useGetDailyAppointment } from "@/services/appointment/hooks/useGetDailyAppointment";
import { getBarberOverview } from "@/services/barberDashboard";
import { BarberHeadline } from "@/services/barberDashboard/types";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import { useSmartAudiences } from "@/services/smartAudience/hooks";
import { IconReceipt2 } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useMemo, useState } from "react";

// Remembered per signed-in phone on this device.
function useHiddenAmounts(phone: string) {
  const key = `home-hide-amounts:${phone}`;
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setHidden((value) => {
      try {
        localStorage.setItem(key, value ? "0" : "1");
      } catch {
        // Storage unavailable: the choice still applies until reload.
      }
      return !value;
    });
  return [hidden, toggle] as const;
}

// Opens the calendar's booking form at that time for this stylist (see the calendar page).
const bookingPath = (gap: DayGap, memberId?: number) =>
  `/dashboard/calendar?add=${bookingTime(gap.start)}${memberId ? `&member=${memberId}` : ""}`;

const toItem = (visit: DayVisit, now: Date, isNext: boolean, gap?: DayGap, memberId?: number): UpcomingItem => {
  const minutes = minutesUntil(visit.start, now);
  return {
    key: visit.key,
    time: clockOf(visit.start),
    duration: durationText(Math.round((visit.end.getTime() - visit.start.getTime()) / 60_000)),
    customer: visit.customer,
    detail: visit.service,
    phone: visit.phone,
    chip: isNext && minutes <= 60 ? { text: `${spanText(minutes)} دیگر`, tone: "soon" } : undefined,
    gapBefore: gap && { from: clockOf(gap.start), text: `${spanText(gap.minutes)} وقت خالی`, to: bookingPath(gap, memberId) },
  };
};

// A stylist's day, kept quiet: the visit in progress or next on top, the rest of today below, then two
// numbers and at most two reminders. Creating and navigating stay in the bottom bar.
// Managers who also take clients see it as «برنامه‌ی من»; `top` then replaces the greeting and
// `memberScope` (their own team id) narrows the SMS hints to their customers.
export default function StylistHome({ top, memberScope }: { top?: ReactNode; memberScope?: number }) {
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const member = useGetMerchantForMerchantUser(phone);
  const { data: merchant } = useGetMerchant();
  // The salon doesn't owe its owner, so their second number is the month's work instead.
  const isOwner = member.data?.merchantUserType === MerchantUserType.Value0;
  // A chair renter runs their own business inside the salon: they get the sales card, with their own figures.
  const isRenter = Boolean((member.data as { isChairRenter?: boolean } | undefined)?.isChairRenter);
  // Same key and filter as the stylist's report page, so both share one request.
  const report = useQuery({
    queryKey: ["barber-dashboard", {}],
    queryFn: () => getBarberOverview({}),
    retry: false,
  });
  const today = useMemo(() => isoDate(new Date()), []);
  // A stylist's calendar only holds their own visits, a manager's the whole team's: hence the member filter.
  const calendar = useGetDailyAppointment({ fromDate: today, toDate: today });
  const now = useNow();
  const memberId = member.data?.id;
  const [hidden, toggleHidden] = useHiddenAmounts(phone);

  const badges = useSectionBadges({ onlineBookings: Boolean(memberId), pendingReviews: false, merchantUserId: memberId });
  // Their own customers: a stylist's scope is set by the server, a manager's «برنامه‌ی من» asks for it.
  const audiences = useSmartAudiences({ take: 2, merchantUserId: memberScope, enabled: !top || memberScope !== undefined });

  // Waits for the member: without their id every visit in a manager's calendar would count as theirs.
  const day = useMemo(
    () => (calendar.data && memberId !== undefined ? stylistDay(calendar.data, memberId, now) : undefined),
    [calendar.data, memberId, now],
  );
  // Visits on later days, from the report (it already carries the next few).
  const later = useMemo(
    () => upcomingFromReport(report.data?.schedule?.upcomingAppointments, now).filter((item) => item.chip?.tone === "day"),
    [report.data, now],
  );
  // The card above already shows the visit in progress, or the next one when nothing is in progress.
  const restOfToday = useMemo(() => {
    if (!day) return [];
    const list = day.live ? day.upcoming : day.upcoming.slice(1);
    const gapBefore = new Map(day.gaps.map((gap) => [gap.key, gap]));
    return list.map((visit) => toItem(visit, now, Boolean(day.live) && visit === day.next, gapBefore.get(visit.key), memberId));
  }, [day, now, memberId]);
  const stillToday = Boolean(day && (day.live || day.next));
  const reminders = useMemo(() => {
    const list: Suggestion[] = [];
    const fresh = badges.onlineBookings ?? 0;
    if (fresh > 0)
      list.push({
        key: "onlineBookings",
        tone: "sales",
        icon: IconReceipt2,
        title: `${faNumber(fresh)} نوبت آنلاین جدید`,
        text: "",
        action: "مشاهده‌ی نوبت‌های آنلاین",
        to: "/dashboard/appointmentDeposit",
      });
    if (audiences.isLoading) return list;
    // Where the smart-audience API is missing (production lags dev), the older hints take its place.
    if (audiences.isError) return [...list, ...stylistSuggestions(report.data)];
    return [
      ...list,
      ...(audiences.data ?? []).map((audience) => audienceSuggestion(audience, { personal: true, merchantUserId: memberScope, now })),
    ];
  }, [badges.onlineBookings, report.data, audiences.isLoading, audiences.isError, audiences.data, memberScope, now]);
  const firstName = member.data?.firstName?.trim();

  return (
    <div className={classes.page}>
      {top ?? (
        <HomeHeader
          title={member.isLoading ? undefined : firstName ? `سلام، ${firstName}` : "سلام"}
          avatarText={firstName?.charAt(0) ?? ""}
          subtitle={[todayTitle(), merchant?.businessInfo?.title].filter(Boolean).join(" · ")}
        />
      )}

      {isRenter && report.data?.headline && <RenterSales headline={report.data.headline} />}

      <NowCard
        day={day}
        now={now}
        isLoading={calendar.isLoading || member.isLoading}
        isError={calendar.isError || member.isError}
        onRetry={() => {
          void calendar.refetch();
          void member.refetch();
        }}
        earningsText={hidden || !report.data ? undefined : compactRialText(report.data.headline?.todayEarnings?.value)}
      />

      {day && <DayProgress day={day} />}

      {day && day.uninvoiced.length > 0 && <InvoiceReminder visits={day.uninvoiced} />}

      {stillToday ? (
        restOfToday.length > 0 && (
          <UpcomingAppointments
            title="ادامه‌ی امروز"
            meta={`${faNumber(restOfToday.length)} نوبت`}
            items={restOfToday.slice(0, 4)}
            moreText={restOfToday.length > 4 ? `${faNumber(restOfToday.length - 4)} نوبت دیگر در تقویم` : undefined}
            isLoading={false}
            isError={false}
            onRetry={() => void calendar.refetch()}
            empty={null}
          />
        )
      ) : (
        day &&
        later.length > 0 && (
          <UpcomingAppointments
            title="نوبت‌های بعدی"
            items={later.slice(0, 3)}
            moreText={later.length > 3 ? `${faNumber(later.length - 3)} نوبت دیگر در تقویم` : undefined}
            isLoading={false}
            isError={false}
            onRetry={() => void report.refetch()}
            empty={null}
          />
        )
      )}

      {!isRenter && <MyEarnings
        report={report.data}
        isLoading={report.isLoading}
        isError={report.isError}
        onRetry={() => void report.refetch()}
        hidden={hidden}
        onToggleHidden={toggleHidden}
        isOwner={isOwner}
      />}

      <Suggestions title="یادآوری‌ها" items={reminders} variant="list" limit={3} />
    </div>
  );
}

// The manager's card, for a chair renter: their own sales, visits and filled capacity. The SMS credit
// band stays off; the credit is the salon's.
function RenterSales({ headline }: { headline: BarberHeadline }) {
  const month = compactRial(headline.monthEarnings?.value);
  return (
    <HeroCard
      ariaLabel="خلاصه‌ی امروز"
      label="فروش امروز"
      amount={headline.todayEarnings?.value}
      note={moneyNote(headline.todayEarnings, "هنوز فروشی ثبت نشده")}
      link={{ to: "/dashboard/barber-overview", label: "گزارش کامل" }}
    >
      <HeroStats>
        <HeroStat
          label="نوبت‌های امروز"
          value={faNumber(headline.todayAppointments?.completed)}
          suffix={`از ${faNumber(headline.todayAppointments?.total)}`}
          progress={percentOf(headline.todayAppointments?.completed ?? 0, headline.todayAppointments?.total ?? 0)}
        />
        <HeroStat
          label="ظرفیت پرشده"
          value={headline.capacity?.hasSchedule ? `${faNumber(headline.capacity.fillRatePercent)}٪` : "—"}
          progress={headline.capacity?.hasSchedule ? headline.capacity.fillRatePercent : undefined}
          hint={headline.capacity?.hasSchedule ? undefined : "شیفتی ثبت نشده"}
        />
        <HeroStat label="فروش این ماه" value={month.value} suffix={month.unit} />
      </HeroStats>
    </HeroCard>
  );
}
